import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import editIcon from "../../../assets/edit-icon.svg";
import eyeIcon from "../../../assets/eye.svg";
import searchIcon from "../../../assets/search-new-icon.svg";
import CLOSE from "../../../assets/CLOSE.svg";
import downIcon from "../../../assets/down-icon.svg";
import Switch from "@mui/material/Switch";
import CustomPagination from "../../../utils/paginationUtils";
import { ProcedureFlowServices } from "../../../services/ProcedureFlowServices";
import { handleApiErrorResponses } from "../../../utils/ApisErrorHandling";
import { FilterDropdown } from "./FilterDropdown";
import InactiveDocumentTypeMappingModal from "./InactiveDocumentTypeMappingModal";

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 400;
const MAPPED_FILTER_STORAGE_KEY = "manageDocxTypeMapping_mappedFilter";

export default function ManageDocumentTypeMapping() {
  const [panelVisible, setPanelVisible] = useState(false);
  const [panelType, setPanelType] = useState("add"); // "add" | "edit" | "view"
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [searchKey, setSearchKey] = useState("");
  const [selectedDocumentType, setSelectedDocumentType] = useState("");
  const [selectedDocumentSubType, setSelectedDocumentSubType] = useState("");
  const [documentType, setDocumentType] = useState([]);
  const [documentSubType, setDocumentSubType] = useState([]);
  const [loadingSubTypes, setLoadingSubTypes] = useState(false);
  const [currentDocument, setCurrentDocument] = useState({
    documentName: "",
    documentType: [],
    documentSubType: "",
    documentId: "",
  });
  const [saving, setSaving] = useState(false);
  const [inactiveModalOpen, setInactiveModalOpen] = useState(false);
  const [docToInactive, setDocToInactive] = useState(null);
  const [mappedProcedures, setMappedProcedures] = useState([]);

  const [allDocuments, setAllDocuments] = useState([]);
  /** All document names for Add/Edit panel dropdown (from v2, no pagination) */
  const [documentNameDropdownList, setDocumentNameDropdownList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [initialDocForEdit, setInitialDocForEdit] = useState(null);
  const [addDocModalOpen, setAddDocModalOpen] = useState(false);
  const [addDocPrefill, setAddDocPrefill] = useState("");
  const [mappedFilter, setMappedFilterState] = useState(() => {
    try {
      const stored = sessionStorage.getItem(MAPPED_FILTER_STORAGE_KEY);
      if (["Mapped documents", "Unmapped documents"].includes(stored)) return stored;
    } catch (_) {}
    return "";
  });
  const setMappedFilter = (v) => {
    setMappedFilterState(v);
    try {
      sessionStorage.setItem(MAPPED_FILTER_STORAGE_KEY, v);
    } catch (_) {}
  };
  const searchDebounceRef = useRef(null);

  const normalizeDocName = (s) => (s ?? "").replace(/\s+/g, " ").trim().toLowerCase();

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearchKey(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchInput]);

  const fetchDocumentNameList = async () => {
    try {
      setLoading(true);
      const response = await ProcedureFlowServices.getGroupedDefaultDocumentNameListV2();
      const groupedDocs = response?.data?.groupedDocs ?? {};
      const unmappedDocs = response?.data?.unmappedDocs ?? [];

      const listFromGrouped = Object.entries(groupedDocs).map(([docName, entries]) => {
        const activeEntries = entries.filter((e) => e.status === "ACTIVE");
        const first = activeEntries[0] || entries[0] || {};
        return {
          id: first.id ?? first.documentId,
          documentId: first.documentId,
          documentName: docName,
          documentTypes: entries.map((e) => e.documentType).filter(Boolean),
          documentSubType: first.documentSubType ?? "—",
          status: activeEntries.length > 0 ? "ACTIVE" : (entries[0]?.status ?? "ACTIVE"),
          entries,
        };
      });

      const listFromUnmapped = unmappedDocs.map((d) => ({
        id: d.id,
        documentId: d.id,
        documentName: d.documentName ?? "",
        documentTypes: [],
        documentSubType: d.documentSubType ?? "—",
        status: d.status ?? "ACTIVE",
        entries: [],
      }));

      const list = [...listFromGrouped, ...listFromUnmapped];
      setAllDocuments(list);
      setTotalCount(list.length);
      setTotalPages(Math.max(1, Math.ceil(list.length / PAGE_SIZE)));
    } catch (err) {
      console.error("Error fetching document name list:", err);
      setAllDocuments([]);
      toast.error("Failed to load document list.");
      setTotalPages(1);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocumentNameList();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [selectedDocumentType, selectedDocumentSubType, mappedFilter]);

  const filteredDocuments = (allDocuments ?? []).filter((doc) => {
    const matchName = !searchKey || (doc.documentName ?? "").toLowerCase().includes(searchKey.toLowerCase());
    const matchType = !selectedDocumentType || (doc.documentTypes ?? []).includes(selectedDocumentType);
    const matchSubType =
      !selectedDocumentSubType || (doc.documentSubType ?? "").trim() === selectedDocumentSubType;
    const isMapped = (doc.documentTypes ?? []).length > 0;
    const matchMapped =
      !mappedFilter ||
      (mappedFilter === "Unmapped documents" && !isMapped) ||
      (mappedFilter === "Mapped documents" && isMapped);
    return matchName && matchType && matchSubType && matchMapped;
  });

  const filteredTotalPages = Math.max(1, Math.ceil(filteredDocuments.length / PAGE_SIZE));
  const paginatedDocuments = filteredDocuments.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );


  const handleSave = async () => {
    const documentName = (currentDocument?.documentName ?? "").trim();
    const selectedTypes = Array.isArray(currentDocument?.documentType) ? currentDocument.documentType : [];
    const documentSubType = (currentDocument?.documentSubType ?? "").trim();
    const documentId = String(currentDocument?.documentId ?? currentDocument?.id ?? "");

    if (!documentName || selectedTypes.length === 0 || !documentSubType) {
      toast.error("Please fill Document Name, select at least one Document Type and Document Sub-Type.");
      return;
    }
    if (panelType === "edit" && !documentId) {
      toast.error("Cannot update: missing document id.");
      return;
    }
    if (panelType === "add" && !documentId) {
      toast.error("Please select a document from the Document Name list.");
      return;
    }

    setSaving(true);
    try {
      const payload = selectedTypes.map((dt) => ({
        documentName,
        documentType: dt,
        documentId,
        id: documentId,
        documentSubType,
      }));
      await ProcedureFlowServices.updateDocTypeMapping(payload);
      toast.success(
        panelType === "edit"
          ? "Document mapping updated successfully."
          : "Document mapping added successfully."
      );
      setPanelVisible(false);
      setCurrentDocument({
        documentName: "",
        documentType: [],
        documentSubType: "",
        documentId: "",
      });
      setInitialDocForEdit(null);
      await fetchDocumentNameList();
    } catch (error) {
      handleApiErrorResponses(error);
      toast.error("Failed to save document mapping. Please try again.");
    } finally {
      setSaving(false);
    }
  };


  const openAdd = () => {
    setPanelVisible(true);
    setPanelType("add");
    setCurrentDocument({
      documentName: "",
      documentType: [],
      documentSubType: "",
      documentId: "",
    });
    setInitialDocForEdit(null);
  };

  const openEdit = (doc) => {
    const isUnmapped = !(doc.documentTypes?.length);
    let documentId = String(doc?.documentId ?? doc?.id ?? "");
    if (!documentId && doc.documentName) {
      const match = (documentNameDropdownList || []).find(
        (d) =>
          normalizeDocName(d.documentName) === normalizeDocName(doc.documentName) &&
          (d.documentSubType ?? "—") === (doc.documentSubType ?? "—")
      );
      documentId = match?.documentId ?? "";
    }
    const docData = {
      ...doc,
      documentId,
      documentType: doc.documentTypes ?? (doc.documentType ? [doc.documentType] : []),
    };
    setPanelVisible(true);
    setPanelType(isUnmapped ? "add" : "edit");
    setCurrentDocument(docData);
    setInitialDocForEdit(docData);
  };

  const openView = (doc) => {
    setPanelVisible(true);
    setPanelType("view");
    setCurrentDocument(doc);
  };

  const openInactiveModal = async (doc) => {
    const targetId = doc?.id ?? doc?.documentId;
    setDocToInactive(doc);
    setMappedProcedures([]);
    try {
      const res = await ProcedureFlowServices.getDocumentLinkedProcedures({
        isDocumentTypeId: false,
        targetId,
        page: 1,
        limit: 100,
      });
      const raw = res?.data?.mappedProcedures ?? [];
      const procedures = raw.map((item) => ({
        id: item.id,
        procedureSchemeId: item.procedureSchemeId,
        documentTypeMappingId: item.documentTypeMappingId,
        procedure: item?.proceduresschememaster?.proceduremaster?.procedure ?? item.procedure ?? "",
        procedureId: item?.proceduresschememaster?.procedureId ?? item.procedureId,
      }));
      setMappedProcedures(procedures);
      console.log("document-linked-procedures FULL response:", res);
      console.log("document-linked-procedures res.data:", JSON.stringify(res?.data, null, 2));
      console.log("document-linked-procedures mappedProcedures (for modal):", procedures);
    } catch (err) {
      console.log("document-linked-procedures error:", err);
    }
    setInactiveModalOpen(true);
  };

  const closeInactiveModal = () => {
    setInactiveModalOpen(false);
    setDocToInactive(null);
    setMappedProcedures([]);
  };

  const handleConfirmInactive = async (selectedProcedures = []) => {
    console.log("handleConfirmInactive - doc:", docToInactive);
    console.log("handleConfirmInactive - selectedProcedures:", selectedProcedures);
    if (!docToInactive) {
      closeInactiveModal();
      return;
    }
    const documentTypeMappingId = String(docToInactive.id ?? docToInactive.documentId ?? "");
    try {
      // API 1: update-doctype-procedure-mapping?task=mapping - for selected procedures only (dedupe by procedureSchemeId)
      if (selectedProcedures.length > 0 && documentTypeMappingId) {
        const seen = new Set();
        const procedureMappingPayload = selectedProcedures
          .map((p) => ({
            procedureSchemeId: String(p.procedureSchemeId ?? p.id ?? "").trim(),
            documentTypeMappingId: String(p.documentTypeMappingId ?? documentTypeMappingId).trim(),
          }))
          .filter((item) => {
            if (!item.procedureSchemeId || !item.documentTypeMappingId) return false;
            if (seen.has(item.procedureSchemeId)) return false;
            seen.add(item.procedureSchemeId);
            return true;
          });
        if (procedureMappingPayload.length > 0) {
          console.log("updateDocTypeProcedureMapping payload:", procedureMappingPayload);
          const procRes = await ProcedureFlowServices.updateDocTypeProcedureMapping(
            procedureMappingPayload,
            "mapping"
          );
          console.log("updateDocTypeProcedureMapping response:", procRes?.data);
        } else {
          console.log("updateDocTypeProcedureMapping - skipped, no valid procedureSchemeId/documentTypeMappingId");
        }
      }

      // API 2: updateDocTypeMapping - status INACTIVE for all entries of this document
      const entries = docToInactive.entries ?? [];
      const docPayload = entries.length > 0
        ? entries.map((entry) => ({
            documentName: entry.documentName ?? docToInactive.documentName ?? "",
            documentType: entry.documentType ?? "",
            documentSubType: entry.documentSubType ?? docToInactive.documentSubType ?? "—",
            documentId: String(entry.documentId ?? docToInactive.documentId ?? ""),
            id: String(entry.id ?? ""),
            status: "INACTIVE",
          }))
        : [{
            documentName: docToInactive.documentName ?? "",
            documentType: (docToInactive.documentTypes ?? [])[0] ?? "",
            documentSubType: docToInactive.documentSubType ?? "—",
            documentId: String(docToInactive.documentId ?? docToInactive.id ?? ""),
            id: documentTypeMappingId,
            status: "INACTIVE",
          }];
      console.log("updateDocTypeMapping payload:", docPayload);
      await ProcedureFlowServices.updateDocTypeMapping(docPayload);
      console.log("updateDocTypeMapping - status INACTIVE done");

      toast.success("Document Type Mapping inactivated successfully.");
      closeInactiveModal();
      await fetchDocumentNameList();
      console.log("inactive completed");
    } catch (err) {
      handleApiErrorResponses(err);
      toast.error("Failed to inactive document type mapping. Please try again.");
      console.log("handleConfirmInactive error:", err);
    }
  };

  const getDocumentTypeOptions = async () => {
    try {
      const response = await ProcedureFlowServices.getMasterDataList("documentType");
      const list = response?.data?.masterData?.allmasterdata ?? [];
      setDocumentType(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("Error fetching document type options:", error);
      setDocumentType([]);
    }
  };

  const getDocumentSubTypeOptions = async (documentTypeValue) => {
    if (!documentTypeValue || !documentTypeValue.trim()) {
      setDocumentSubType([]);
      setSelectedDocumentSubType("");
      return;
    }
    try {
      setLoadingSubTypes(true);
      setSelectedDocumentSubType("");
      const response = await ProcedureFlowServices.getMasterDataList(documentTypeValue.trim());
      const list = response?.data?.masterData?.allmasterdata ?? [];
      setDocumentSubType(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("Error fetching document sub-type options:", error);
      setDocumentSubType([]);
    } finally {
      setLoadingSubTypes(false);
    }
  };

  useEffect(() => {
    getDocumentTypeOptions();
  }, []);

  useEffect(() => {
    getDocumentSubTypeOptions(selectedDocumentType);
  }, [selectedDocumentType]);

  /** Fetch all document names for Add/Edit panel dropdown (no pagination, scroll in dropdown) */
  const fetchAllDocumentNamesForDropdown = async () => {
    try {
      const all = [];
      let page = 1;
      let hasMore = true;
      const limit = 500;
      while (hasMore) {
        const res = await ProcedureFlowServices.getDefaultDocumentNameList({
          status: "ACTIVE",
          page,
          limit,
        });
        const data = res?.data;
        const list = data?.docNameData ?? [];
        const totalPages = data?.totalPages ?? 1;
        list.forEach((item) => {
          all.push({
            documentId: String(item.documentId ?? item.id ?? ""),
            documentName: item.documentName ?? "",
            documentSubType: item.documentSubType ?? "—",
          });
        });
        hasMore = page < totalPages && list.length === limit;
        page += 1;
      }
      setDocumentNameDropdownList(all);
      return all;
    } catch (err) {
      console.error("Error fetching document names for dropdown:", err);
      setDocumentNameDropdownList([]);
      return [];
    }
  };

  useEffect(() => {
    fetchAllDocumentNamesForDropdown();
  }, []);

  /* Sub-type is now auto-filled from document name selection — no separate fetch needed */

  const documentTypeOptions = (documentType ?? []).map((d) => (typeof d === "string" ? d : d.value));
  const docTypeDisplayMap = Object.fromEntries(
    (documentType ?? [])
      .filter((d) => typeof d !== "string" && d.value && d.displayText)
      .map((d) => [d.value, d.displayText])
  );
  /** Include "Document Type" as first option so by default nothing is selected and list shows all data */
  const documentTypeFilterOptions = ["Document Type", ...documentTypeOptions];
  const documentSubTypeOptions = (documentSubType ?? []).map((d) => (typeof d === "string" ? d : d.value));

  /** Unmapped documents: from groupedDocs (entries with null documentType) + unmappedDocs from API */
  const unmappedDocumentsList = (allDocuments ?? []).filter((d) => !(d.documentTypes?.length));
  const mappedFilterOptions = ["Mapped documents", "Unmapped documents"];

  return (
    <div className="w-full min-h-screen min-h-[100dvh] flex flex-col">
      {/* Green gradient background for the entire page */}
      <div className="bg-[radial-gradient(81.98%_338.81%_at_13.41%_276.08%,_rgba(90,198,159,0.5)_0%,_rgba(222,222,222,0.5)_100%)] flex-1 min-h-full w-full pt-6 pb-3 px-3 sm:pt-8 sm:pb-4 sm:px-4 md:pt-12 md:pb-6 md:px-6">
        {/* HEADER: Title + Add Button - on green background */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 sm:gap-6 py-4 sm:py-6 md:py-8 mb-4 sm:mb-6">
          <div className="shrink-0">
            <h1 className="font-lufga font-normal text-[50px] leading-[100%] tracking-[-0.07em] align-middle text-black">
              Manage Document Type Mapping ({(filteredDocuments ?? []).length})
            </h1>
            <p className="font-lufga text-base text-gray-600 mt-1">
              Total unmapped documents: {(unmappedDocumentsList ?? []).length}
            </p>
          </div>

          <div className="flex items-center gap-3 sm:gap-4 lg:gap-6">
            <button
              onClick={openAdd}
              className="font-lufga w-[237px] h-[60px] rounded-[54px] gap-[10px] px-6 border-2 border-solid border-[#5AC69F] bg-[#5AC69FCC] flex items-center justify-center font-medium text-black hover:opacity-90 transition-opacity shrink-0"
            >
              <span className="text-xl leading-none">+</span>
              Add New Mapping
            </button>
          </div>
        </div>

        {/* FILTERS ROW */}
        <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-4 sm:gap-6 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 lg:gap-6">
            {/* <select
              className="w-full sm:w-[260px] h-12 sm:h-14 px-4 sm:px-6 border border-solid border-[#E0E0E0] rounded-[40px] bg-white font-lufga text-sm sm:text-base outline-none"
            >
              <option value="">Document Type</option>
              <option value="Admission">Admission</option>
              <option value="Enhancement">Enhancement</option>
              <option value="Claim / Discharge">Claim / Discharge</option>
            </select> */}
          <FilterDropdown
            placeholder="Document Type"
            options={documentTypeFilterOptions}
            value={selectedDocumentType}
            setValue={(v) => setSelectedDocumentType(v === "Document Type" ? "" : v)}
            labelMap={docTypeDisplayMap}
            hideLabel
          />
          <div className="flex items-center gap-0.5">
            <FilterDropdown
              placeholder={loadingSubTypes ? "Loading..." : "Document Sub-Type"}
              options={documentSubTypeOptions}
              value={selectedDocumentSubType}
              setValue={setSelectedDocumentSubType}
              disabled={loadingSubTypes || !selectedDocumentType}
            />
            <FilterDropdown
              placeholder="Document type mapping"
              options={mappedFilterOptions}
              value={mappedFilter}
              setValue={setMappedFilter}
              clearValue=""
              hideLabel
            />
          </div>
            {/* <select
              className="w-full sm:w-[260px] h-12 sm:h-14 px-4 sm:px-6 border border-solid border-[#E0E0E0] rounded-[40px] bg-white font-lufga text-sm sm:text-base outline-none"
            >
              <option value="">Document Sub-Type</option>
              <option value="Imaging">Imaging</option>
              <option value="Progress Notes">Progress Notes</option>
              <option value="Discharge Summary">Discharge Summary</option>
            </select> */}
          </div>

          <div className="relative flex-1 lg:flex-initial lg:max-w-[520px] min-w-0">
            <img
              src={searchIcon}
              alt="search"
              className="absolute left-4 sm:left-6 top-1/2 -translate-y-1/2 w-5 h-5 sm:w-6 sm:h-6 pointer-events-none"
            />
            <input
              type="text"
              placeholder="Document Name"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full h-12 sm:h-14 pl-11 sm:pl-14 pr-5 sm:pr-6 border border-solid border-[#E0E0E0] rounded-[54px] outline-none font-lufga text-sm sm:text-base text-[#1B1B1B] placeholder:text-gray-400 bg-white"
            />
          </div>
        </div>

        {/* MAIN AREA: White card for table */}
        <div
          className={`grid gap-6 transition-all duration-300 ${
            panelVisible ? "lg:grid-cols-[1fr_minmax(35vw,560px)]" : "grid-cols-1"
          }`}
        >
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-sm overflow-x-auto p-4 sm:p-6 min-w-0">
            <div className="grid grid-cols-[56px_minmax(200px,2fr)_150px_minmax(200px,2fr)_120px] gap-3 min-w-[850px] px-0 sm:px-2 py-3 font-lufga font-semibold text-sm sm:text-base text-[#1B1B1B] border-b border-[#E0E0E0]">
              <span>SNo.</span>
              <span className="text-left">Document Name</span>
              <span className="text-left">Sub-Type</span>
              <span className="text-left">Type</span>
              <span className="text-right pr-2">Actions</span>
            </div>

            <div className="font-lufga divide-y divide-[#E8E8E8]">
              {loading ? (
                <div className="py-12 text-center text-gray-500 font-lufga text-sm sm:text-base">
                  Loading document list...
                </div>
              ) : (allDocuments ?? []).length === 0 ? (
                <div className="py-12 text-center text-gray-500 font-lufga text-sm sm:text-base">
                  No documents found.
                </div>
              ) : (filteredDocuments ?? []).length === 0 ? (
                <div className="py-12 text-center text-gray-500 font-lufga text-sm sm:text-base">
                  No documents match the selected filters.
                </div>
              ) : (
                (paginatedDocuments ?? []).map((doc, index) => (
                  <DocumentRow
                    key={doc.id ?? doc.documentId ?? index}
                    index={(page - 1) * PAGE_SIZE + index}
                    doc={doc}
                    docTypeDisplayMap={docTypeDisplayMap}
                    onEdit={() => openEdit(doc)}
                    onView={() => openView(doc)}
                    onToggleInactive={() => openInactiveModal(doc)}
                    onReactivate={async () => {
                      try {
                        const entries = doc.entries ?? [];
                        const docPayload = entries.map((entry) => ({
                          documentName: entry.documentName ?? doc.documentName ?? "",
                          documentType: entry.documentType ?? "",
                          documentSubType: entry.documentSubType ?? doc.documentSubType ?? "—",
                          documentId: String(entry.documentId ?? doc.documentId ?? ""),
                          id: String(entry.id ?? ""),
                          status: "ACTIVE",
                        }));
                        if (docPayload.length === 0) {
                          docPayload.push({
                            documentName: doc.documentName ?? "",
                            documentType: (doc.documentTypes ?? [])[0] ?? "",
                            documentSubType: doc.documentSubType ?? "—",
                            documentId: String(doc.documentId ?? doc.id ?? ""),
                            id: String(doc.id ?? doc.documentId ?? ""),
                            status: "ACTIVE",
                          });
                        }
                        await ProcedureFlowServices.updateDocTypeMapping(docPayload);
                        toast.success("Document Type Mapping reactivated successfully.");
                        await fetchDocumentNameList();
                      } catch (err) {
                        console.error("Failed to reactivate:", err);
                        toast.error("Failed to reactivate. Please try again.");
                      }
                    }}
                    alternateBg={index % 2 === 1}
                  />
                ))
              )}
            </div>

            {!loading && (filteredDocuments ?? []).length > 0 && (
              <div className="flex justify-center mt-6 mb-4 sm:mb-6">
                <CustomPagination
                  page={page}
                  setPage={setPage}
                  totalPages={filteredTotalPages}
                />
              </div>
            )}
          </div>

          {panelVisible && (
            <div className="bg-white rounded-2xl sm:rounded-3xl border border-[#E0E0E0] p-6 sm:p-8 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-lufga text-[32px] font-normal">
                  {panelType === "add"
                    ? "Add Document Type Mapping"
                    : panelType === "edit"
                    ? "Edit Document Type Mapping"
                    : "View Document Type Mapping"}
                </h2>
                <img
                  src={CLOSE}
                  alt="close"
                  onClick={() => setPanelVisible(false)}
                  className="w-6 h-6 cursor-pointer"
                />
              </div>

              {/* ADD / EDIT */}
              {(panelType === "add" || panelType === "edit") && (
                <>
                  <PanelDropdown
                    placeholder="Document Name"
                    options={documentNameDropdownList}
                    value={currentDocument?.documentName || ""}
                    labelKey="documentName"
                    onSelect={(item) => {
                      const itemName = (item.documentName ?? "").replace(/\s+/g, " ").trim().toLowerCase();
                      const match = allDocuments.find(
                        (d) => (d.documentName ?? "").replace(/\s+/g, " ").trim().toLowerCase() === itemName
                      );
                      setCurrentDocument((prev) => ({
                        ...prev,
                        documentName: item.documentName ?? "",
                        documentId: item.documentId ?? "",
                        documentSubType: item.documentSubType || "—",
                        documentType: match?.documentTypes?.length ? [...match.documentTypes] : [],
                        _alreadyMapped: !!(match?.documentTypes?.length),
                      }));
                    }}
                    className="mb-6"
                    onAddNew={(searchText) => {
                      setAddDocPrefill(searchText);
                      setAddDocModalOpen(true);
                    }}
                    onClear={() =>
                      setCurrentDocument({
                        ...currentDocument,
                        documentName: "",
                        documentId: "",
                        documentSubType: "",
                        documentType: [],
                        _alreadyMapped: false,
                      })
                    }
                  />

                  {/* Document Sub-Type: auto-filled from selected document name, non-editable */}
                  <div className="relative mb-6">
                    <div
                      className={`flex items-center h-[70px] px-6 pr-40 border-2 border-solid rounded-[40px] font-lufga text-[16px] cursor-not-allowed ${
                        currentDocument?.documentSubType
                          ? "border-gray-200 bg-gray-50 text-[#1B1B1B]"
                          : "border-gray-200 bg-gray-50 text-gray-400"
                      }`}
                    >
                      {currentDocument?.documentSubType || "Document Sub-Type"}
                    </div>
                    {currentDocument?.documentSubType && (
                      <span className="absolute right-6 top-1/2 -translate-y-1/2 font-lufga text-[13px] text-gray-400 pointer-events-none">
                        Document Sub-Type
                      </span>
                    )}
                  </div>

                  <DocTypeMultiSelect
                    options={documentType}
                    selectedValues={Array.isArray(currentDocument?.documentType) ? currentDocument.documentType : []}
                    onChange={(selected) =>
                      setCurrentDocument({
                        ...currentDocument,
                        documentType: selected,
                      })
                    }
                    docTypeDisplayMap={docTypeDisplayMap}
                  />

                  {(() => {
                    const currentTypes = Array.isArray(currentDocument?.documentType) ? [...currentDocument.documentType].sort() : [];
                    const initialTypes = Array.isArray(initialDocForEdit?.documentType) ? [...initialDocForEdit.documentType].sort() : [];
                    const typesChanged = currentTypes.length !== initialTypes.length || currentTypes.some((t, i) => t !== initialTypes[i]);
                    const hasChanges =
                      panelType !== "edit" ||
                      !initialDocForEdit ||
                      (currentDocument?.documentName ?? "").trim() !== (initialDocForEdit?.documentName ?? "").trim() ||
                      typesChanged ||
                      (currentDocument?.documentSubType ?? "").trim() !== (initialDocForEdit?.documentSubType ?? "").trim() ||
                      String(currentDocument?.documentId ?? "") !== String(initialDocForEdit?.documentId ?? "");
                    const isAlreadyMapped = !!(panelType === "add" && currentDocument?._alreadyMapped);
                    return (
                      <>
                        {isAlreadyMapped && (
                          <p className="text-center text-sm text-amber-600 font-lufga mb-3">
                            This document is already mapped. You can edit the document types below and save to update.
                          </p>
                        )}
                        <button
                          onClick={handleSave}
                          disabled={saving || !hasChanges}
                          className="w-full h-[70px] border-2 border-solid border-[#5AC69F] bg-emerald-400 rounded-[40px] font-lufga font-semibold hover:bg-emerald-500 transition disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {saving ? "Saving..." : panelType === "edit" ? "Save Changes" : "Add Mapping"}
                        </button>
                      </>
                    );
                  })()}
                </>
              )}

              {/* VIEW */}
              {panelType === "view" && (
                <div className="font-lufga text-gray-700 space-y-3">
                  <div>
                    <strong>Document Name:</strong>{" "}
                    {currentDocument?.documentName}
                  </div>
                  <div>
                    <strong>Document Type:</strong>{" "}
                    {(() => {
                      const types = currentDocument?.documentTypes ?? (Array.isArray(currentDocument?.documentType) ? currentDocument.documentType : []);
                      return types.length > 0 ? types.map((t) => docTypeDisplayMap[t] || t).join(", ") : "—";
                    })()}
                  </div>
                  <div>
                    <strong>Document Sub-Type:</strong>{" "}
                    {currentDocument?.documentSubType}
                  </div>
                  <div>
                    <strong>Created On:</strong> 2026-01-23
                  </div>
                  <div>
                    <strong>Created By:</strong> Admin
                  </div>
                  <div>
                    <strong>Upload On:</strong> 2026-01-23
                  </div>
                  <div>
                    <strong>Upload By:</strong> Admin
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {inactiveModalOpen && (
          <InactiveDocumentTypeMappingModal
            onClose={closeInactiveModal}
            onConfirm={handleConfirmInactive}
            procedures={mappedProcedures}
          />
        )}

        {addDocModalOpen && (
          <AddDocumentModal
            prefillName={addDocPrefill}
            existingDocuments={documentNameDropdownList}
            onClose={() => setAddDocModalOpen(false)}
            onSaved={async (newDoc) => {
              setAddDocModalOpen(false);
              const [, freshList] = await Promise.all([
                fetchDocumentNameList(),
                fetchAllDocumentNamesForDropdown(),
              ]);
              const match = (freshList || []).find(
                (d) => d.documentName?.toLowerCase() === newDoc.documentName?.toLowerCase()
              );
              const resolvedId = match?.documentId || newDoc.documentId || "";
              const resolvedSubType = match?.documentSubType || newDoc.documentSubType || "—";
              setCurrentDocument((prev) => ({
                ...prev,
                documentName: newDoc.documentName,
                documentId: resolvedId,
                documentSubType: resolvedSubType,
              }));
            }}
          />
        )}
      </div>
    </div>
  );
}

/* Scrollable dropdown for Add/Edit panel with search: prevents list from taking full page height */
function PanelDropdown({
  placeholder,
  options = [],
  value,
  onSelect,
  labelKey,
  disabled = false,
  className = "",
  onAddNew,
  onClear,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const ref = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (open && searchInputRef.current) {
      searchInputRef.current.focus();
    }
    if (!open) setHighlightedIndex(-1);
  }, [open]);

  const displayValue = value || placeholder;
  const isObjectOptions = options.length > 0 && typeof options[0] === "object" && options[0] !== null;

  // Filter options based on search
  const filteredOptions = options.filter((opt) => {
    if (!search.trim()) return true;
    const label = isObjectOptions && labelKey ? opt[labelKey] : opt;
    const labelStr = typeof label === "string" ? label : String(label ?? "");
    return labelStr.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div ref={ref} className={`relative w-full ${className}`}>
      <div
        onClick={() => !disabled && setOpen(!open)}
        className={`w-full h-[70px] px-6 border-2 border-solid border-gray-200 rounded-[40px] outline-none font-lufga text-[16px] flex items-center bg-white ${
          disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"
        }`}
      >
        <span className={`flex-1 min-w-0 truncate ${value ? "text-gray-900" : "text-gray-400"}`}>{displayValue}</span>
        {value && (
          <span className="font-lufga text-[13px] text-gray-400 pointer-events-none shrink-0 mr-2">
            {placeholder}
          </span>
        )}
        {value && onClear && !disabled && (
          <span
            onClick={(e) => {
              e.stopPropagation();
              onClear();
            }}
            className="flex items-center justify-center w-5 h-5 rounded-full hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors text-xs font-bold cursor-pointer shrink-0 mr-1"
            title="Clear selection"
          >
            ✕
          </span>
        )}
        {!disabled && (
          <span className="shrink-0 text-gray-400">▾</span>
        )}
      </div>
      {open && (
        <div
          className="absolute z-30 left-0 right-0 mt-2 bg-white rounded-2xl shadow-lg border border-gray-200"
        >
          {/* Search input */}
          <div className="px-4 pt-3 pb-2 border-b border-gray-200">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setHighlightedIndex(-1); }}
              onKeyDown={(e) => {
                if (filteredOptions.length === 0) return;
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  setHighlightedIndex((prev) => prev < filteredOptions.length - 1 ? prev + 1 : 0);
                } else if (e.key === "ArrowUp") {
                  e.preventDefault();
                  setHighlightedIndex((prev) => prev > 0 ? prev - 1 : filteredOptions.length - 1);
                } else if (e.key === "Enter" && highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
                  e.preventDefault();
                  const opt = filteredOptions[highlightedIndex];
                  onSelect(isObjectOptions ? opt : opt);
                  setOpen(false);
                  setSearch("");
                }
              }}
              onClick={(e) => e.stopPropagation()}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg outline-none font-lufga text-sm focus:border-[#5AC69F]"
            />
          </div>
          
          {/* Options list */}
          <div className="max-h-[240px] overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-6 py-4 text-sm text-gray-500">
                <div>{search.trim() ? "No matching results" : "No options"}</div>
                {search.trim() && onAddNew && (
                  <div className="flex items-center gap-3 mt-2">
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        const query = search.trim();
                        setOpen(false);
                        setSearch("");
                        onAddNew(query);
                      }}
                      className="inline-flex items-center gap-1 text-[#5AC69F] hover:text-emerald-600 font-medium cursor-pointer transition-colors"
                    >
                      <span className="text-lg leading-none">+</span> Add Document
                    </div>
                    <span className="text-gray-300">|</span>
                    <a
                      href="/dashboard/manage-documents"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1 text-[#5AC69F] hover:text-emerald-600 font-medium cursor-pointer transition-colors"
                    >
                      Manage Documents ↗
                    </a>
                  </div>
                )}
              </div>
            ) : (
              filteredOptions.map((opt, idx) => {
                const label = isObjectOptions && labelKey ? opt[labelKey] : opt;
                const labelStr = typeof label === "string" ? label : String(label ?? "");
                const isHighlighted = highlightedIndex === idx;
                return (
                  <div
                    key={labelStr + idx}
                    ref={isHighlighted ? (el) => el?.scrollIntoView({ block: "nearest", behavior: "smooth" }) : undefined}
                    onClick={() => {
                      onSelect(isObjectOptions ? opt : opt);
                      setOpen(false);
                      setSearch("");
                    }}
                    onMouseEnter={() => setHighlightedIndex(idx)}
                    className={`px-6 py-3 text-sm cursor-pointer font-lufga ${
                      isHighlighted ? "bg-[#F0F9F4] ring-1 ring-inset ring-[#5AC69F]" : "hover:bg-[#F4FBF8]"
                    }`}
                  >
                    {labelStr}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* =============== ADD DOCUMENT MODAL (inline, same as Manage Documents) =============== */
function AddDocumentModal({ prefillName = "", existingDocuments = [], onClose, onSaved }) {
  const [docName, setDocName] = useState(prefillName);
  const [docSubType, setDocSubType] = useState("");
  const [subTypeOptions, setSubTypeOptions] = useState([]);
  const [loadingSubTypes, setLoadingSubTypes] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [stOpen, setStOpen] = useState(false);
  const [stSearch, setStSearch] = useState("");
  const [stHighlight, setStHighlight] = useState(-1);
  const stRef = useRef(null);
  const stSearchRef = useRef(null);

  const existingMatch = existingDocuments.find(
    (d) => (d.documentName ?? "").trim().toLowerCase() === docName.trim().toLowerCase()
  );
  const isExisting = !!(docName.trim() && existingMatch);

  useEffect(() => {
    if (isExisting && existingMatch?.documentSubType) {
      setDocSubType(existingMatch.documentSubType);
    }
  }, [isExisting, existingMatch?.documentSubType]);

  useEffect(() => {
    (async () => {
      try {
        const res = await ProcedureFlowServices.getMasterDataList("documentSubType");
        const items = res?.data?.masterData?.allmasterdata || [];
        setSubTypeOptions(
          items.map((i) => (typeof i === "string" ? i : i.value ?? "").trim()).filter(Boolean)
        );
      } catch (_) {
        setSubTypeOptions([]);
      } finally {
        setLoadingSubTypes(false);
      }
    })();
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (stRef.current && !stRef.current.contains(e.target)) {
        setStOpen(false);
        setStSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (stOpen && stSearchRef.current) stSearchRef.current.focus();
    if (!stOpen) { setStSearch(""); setStHighlight(-1); }
  }, [stOpen]);

  const filteredSt = subTypeOptions.filter(
    (o) => !stSearch.trim() || o.toLowerCase().includes(stSearch.toLowerCase())
  );

  const handleSave = async () => {
    const name = docName.trim();
    if (!name) { setError("Document name is required."); return; }
    if (!docSubType) { setError("Document sub type is required."); return; }
    setError(null);
    setSaving(true);
    try {
      const res = await ProcedureFlowServices.addUpdateDefaultDocName([
        { documentName: name, documentSubType: docSubType },
      ]);
      const created = res?.data?.data?.[0] ?? res?.data?.[0] ?? null;
      toast.success("Document added successfully.");
      onSaved({
        documentId: String(created?.id ?? created?.documentId ?? ""),
        documentName: created?.documentName ?? name,
        documentSubType: created?.documentSubType ?? docSubType,
      });
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to add document.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-3xl shadow-2xl w-[95%] max-w-[520px] p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-lufga text-[26px] font-normal">Add New Document</h2>
          <img src={CLOSE} alt="close" onClick={onClose} className="w-6 h-6 cursor-pointer" />
        </div>

        <div className="relative mb-5">
          <input
            type="text"
            placeholder="Name of the Document"
            value={docName}
            onChange={(e) => { setDocName(e.target.value); setError(null); }}
            className="w-full h-[60px] px-6 pr-44 border-2 border-solid border-gray-200 rounded-[40px] outline-none font-lufga text-[15px] bg-white"
          />
          {docName.trim() && (
            <span className="absolute right-6 top-1/2 -translate-y-1/2 font-lufga text-[13px] text-gray-400 pointer-events-none">
              Name of the Document
            </span>
          )}
        </div>

        <div ref={stRef} className="relative mb-5">
          <div
            onClick={() => !loadingSubTypes && !isExisting && setStOpen(!stOpen)}
            className={`flex items-center h-[60px] px-6 border-2 border-gray-200 border-solid rounded-[40px] font-lufga text-[15px] bg-white ${
              loadingSubTypes || isExisting ? "cursor-not-allowed opacity-60" : "cursor-pointer"
            }`}
          >
            <span className={`flex-1 min-w-0 truncate ${docSubType ? "text-[#1B1B1B]" : "text-gray-400"}`}>
              {loadingSubTypes ? "Loading sub types..." : docSubType || "Select Document Sub Type"}
            </span>
            {docSubType && (
              <span className="font-lufga text-[13px] text-gray-400 pointer-events-none shrink-0 mr-2">
                Document Sub Type
              </span>
            )}
            <span className={`shrink-0 text-gray-400 transition-transform ${stOpen ? "rotate-180" : ""}`}>▾</span>
          </div>
          {stOpen && (
            <div className="absolute z-50 left-0 right-0 mt-2 bg-white rounded-2xl shadow-lg border border-gray-200 max-h-[260px] flex flex-col overflow-hidden">
              <div className="px-4 pt-3 pb-2 border-b border-gray-200 shrink-0">
                <input
                  ref={stSearchRef}
                  type="text"
                  value={stSearch}
                  onChange={(e) => { setStSearch(e.target.value); setStHighlight(-1); }}
                  onKeyDown={(e) => {
                    if (filteredSt.length === 0) return;
                    if (e.key === "ArrowDown") { e.preventDefault(); setStHighlight((p) => p < filteredSt.length - 1 ? p + 1 : 0); }
                    else if (e.key === "ArrowUp") { e.preventDefault(); setStHighlight((p) => p > 0 ? p - 1 : filteredSt.length - 1); }
                    else if (e.key === "Enter" && stHighlight >= 0) { e.preventDefault(); setDocSubType(filteredSt[stHighlight]); setStOpen(false); setError(null); }
                  }}
                  onClick={(e) => e.stopPropagation()}
                  placeholder="Search..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg outline-none font-lufga text-sm focus:border-[#5AC69F]"
                />
              </div>
              <div className="overflow-y-auto flex-1">
                {filteredSt.length === 0 ? (
                  <div className="px-6 py-4 text-sm text-gray-500 font-lufga">No results</div>
                ) : (
                  filteredSt.map((opt, idx) => (
                    <div
                      key={opt + idx}
                      ref={stHighlight === idx ? (el) => el?.scrollIntoView({ block: "nearest", behavior: "smooth" }) : undefined}
                      onClick={() => { setDocSubType(opt); setStOpen(false); setError(null); }}
                      onMouseEnter={() => setStHighlight(idx)}
                      className={`px-6 py-3 text-sm cursor-pointer font-lufga ${
                        opt === docSubType
                          ? "bg-[#E8F5E9] text-[#2E7D32] font-medium"
                          : stHighlight === idx
                            ? "bg-[#F0F9F4] ring-1 ring-inset ring-[#5AC69F]"
                            : "hover:bg-[#F4FBF8]"
                      }`}
                    >
                      {opt}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {error && <p className="text-red-500 text-sm font-lufga mb-4">{error}</p>}
        {isExisting && (
          <p className="text-amber-600 text-sm font-lufga mb-4 text-center">
            This document already exists with sub-type "{existingMatch?.documentSubType}".
          </p>
        )}

        <button
          onClick={handleSave}
          disabled={saving || isExisting}
          className="w-full h-[56px] border-2 border-solid border-[#5AC69F] bg-emerald-400 rounded-[40px] font-lufga font-semibold hover:bg-emerald-500 transition disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {saving ? "Saving..." : "Add Document"}
        </button>
      </div>
    </div>
  );
}

/* ROW: SNo. | Document Name | Document Type | Document Sub-Type | Actions */
function DocumentRow({ index, doc, docTypeDisplayMap = {}, onEdit, onView, onToggleInactive, onReactivate, alternateBg }) {
  const isActive = doc?.status === "ACTIVE";
  const [enabled, setEnabled] = useState(isActive);
  const hasEntries = (doc?.entries ?? []).length > 0;

  useEffect(() => {
    setEnabled(isActive);
  }, [isActive]);

  const handleToggle = () => {
    if (enabled) {
      onToggleInactive?.();
    } else {
      onReactivate?.();
      setEnabled(true);
    }
  };

  return (
    <div
      className={`grid grid-cols-[56px_minmax(200px,2fr)_150px_minmax(200px,2fr)_120px] gap-3 min-w-[850px] px-0 sm:px-2 py-4 sm:py-5 items-center font-lufga ${
        alternateBg ? "bg-[#E8E8E833]" : ""
      }`}
    >
      <span className="font-medium text-sm sm:text-base text-[#1B1B1B]">
        {index + 1}
      </span>

      <p className="text-sm sm:text-base text-[#1B1B1B] text-gray-800 leading-relaxed break-words min-w-0 text-left overflow-hidden text-ellipsis">
        {doc.documentName}
      </p>
      <p className="text-sm sm:text-base text-[#1B1B1B] text-gray-800 leading-relaxed break-words min-w-0 text-left overflow-hidden text-ellipsis" title={doc.documentSubType}>
        {doc.documentSubType}
      </p>
      <p className="text-sm sm:text-base text-[#1B1B1B] text-gray-800 leading-relaxed break-words min-w-0 text-left overflow-hidden text-ellipsis" title={(doc.documentTypes ?? []).map((t) => docTypeDisplayMap[t] || t).join(", ")}>
        {(doc.documentTypes ?? []).map((t) => docTypeDisplayMap[t] || t).join(", ") || "—"}
      </p>

      <div className="flex items-center gap-3 sm:gap-4 justify-end pr-2">
        <img
          src={eyeIcon}
          alt="view"
          className="w-5 h-5 cursor-pointer flex-shrink-0"
          onClick={onView}
        />
        <img
          src={editIcon}
          alt="edit"
          className="w-5 h-5 cursor-pointer flex-shrink-0"
          onClick={onEdit}
        />
        <Switch
          checked={enabled}
          onChange={handleToggle}
          size="small"
          sx={{
            "& .MuiSwitch-switchBase.Mui-checked": {
              color: "#5AC69F",
              "& + .MuiSwitch-track": { backgroundColor: "#5AC69F !important", opacity: 0.6 },
            },
            "& .MuiSwitch-track": {
              backgroundColor: "#9e9e9e !important",
            },
          }}
        />
      </div>
    </div>
  );
}

function DocTypeMultiSelect({ options, selectedValues, onChange, docTypeDisplayMap = {}, disabled = false }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const optionValues = options.map((d) => (typeof d === "string" ? d : d.value));

  const toggleOption = (val) => {
    if (selectedValues.includes(val)) {
      onChange(selectedValues.filter((v) => v !== val));
    } else {
      onChange([...selectedValues, val]);
    }
  };

  return (
    <div ref={ref} className="relative mb-10">
      <div
        onClick={() => !disabled && setOpen(!open)}
        className={`flex items-center gap-2 w-full min-h-[70px] px-5 pr-28 border-2 border-solid border-[#E0E0E0] rounded-[40px] font-lufga text-[16px] bg-white py-3 ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
      >
        {selectedValues.length === 0 ? (
          <span className="text-gray-400 flex-1">Document Type</span>
        ) : (
          <div className="flex flex-wrap gap-2 flex-1">
            {selectedValues.map((v) => (
              <span
                key={v}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-[#E8F5E9] text-[#1B1B1B] border border-[#5AC69F66]"
              >
                {docTypeDisplayMap[v] || v}
                {!disabled && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onChange(selectedValues.filter((x) => x !== v));
                    }}
                    className="flex items-center justify-center w-4 h-4 rounded-full hover:bg-[#5AC69F33] text-[#2E7D32] text-[10px] font-bold cursor-pointer bg-transparent border-none leading-none"
                  >
                    ✕
                  </button>
                )}
              </span>
            ))}
          </div>
        )}
        {selectedValues.length > 0 && (
          <span className="absolute right-12 top-1/2 -translate-y-1/2 font-lufga text-[13px] text-gray-400 pointer-events-none">
            Document Type
          </span>
        )}
        <img
          src={downIcon}
          alt="down"
          className={`absolute right-6 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none transition-transform ${open ? "rotate-180" : ""}`}
        />
      </div>

      {open && (
        <div className="absolute z-50 left-0 right-0 mt-2 bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
          {optionValues.map((val) => {
            const isChecked = selectedValues.includes(val);
            const label = docTypeDisplayMap[val] || val;
            return (
              <div
                key={val}
                onClick={() => toggleOption(val)}
                className={`px-6 py-4 flex items-center gap-3 cursor-pointer font-lufga text-sm border-b border-gray-100 last:border-b-0 transition-colors ${
                  isChecked ? "bg-[#E8F5E9]" : "hover:bg-[#F4FBF8]"
                }`}
              >
                <span
                  className={`inline-flex items-center justify-center w-[18px] h-[18px] rounded border-2 text-[11px] font-bold leading-none shrink-0 ${
                    isChecked ? "bg-[#5AC69F] border-[#5AC69F] text-white" : "border-gray-400 bg-white"
                  }`}
                >
                  {isChecked ? "✓" : ""}
                </span>
                <span className={isChecked ? "text-[#2E7D32] font-medium" : "text-[#1B1B1B]"}>
                  {label}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
